import React, { useCallback, useEffect, useMemo, useState } from "react";
import { darkTheme } from "@yleisradio/yds-core";
import { Close, Play } from "@yleisradio/yds-icons-react";
import styled from "styled-components";

import { END_OFFSET } from "../constants";
import { useEmbedParametersContext } from "../contexts/embedParametersContext";
import { useMediaContext } from "../contexts/MediaContext";
import { findCuepoint, isCuepointVisible } from "../helpers/cuepoints";
import { localizedText } from "../helpers/previewHelpers";
import { getTimeRemaining } from "../helpers/timeHelpers";
import i18n from "../service/i18n";
import { usePlayerStore } from "../store/usePlayerStore";
import { buttonStyle } from "../styles/global";
import {
  nextEpisodeBackgroundColor,
  nextEpisodeInfoColor,
  nextEpisodeTimerColor,
  playButtonBackgroundColor
} from "../styles/variables";
import { PlayerUpdateEvent } from "../types/common";
import { Episode } from "../types/player-api";

interface Props {
  isAudio: boolean;
  newPlayerUi?: boolean;
  nextEpisode: Episode;
  update: PlayerUpdateEvent;
}

const NextEpisode = styled.div<{
  isVisible: boolean;
  isAudio: boolean;
  newPlayerUi?: boolean;
}>`
  align-items: center;
  background: ${nextEpisodeBackgroundColor};
  bottom: ${props =>
    props.isAudio ? "0" : props.newPlayerUi ? "80px" : "48px"};
  display: flex;
  max-width: 100%;
  opacity: ${props => (props.isVisible ? "1" : "0")};
  visibility: ${props => (props.isVisible ? "initial" : "hidden")};
  padding: 8px 0;
  position: absolute;
  right: 0;
  transition: opacity 0.4s linear;
  width: 330px;
  z-index: 3;
  cursor: default;
`;

const NextEpisodeInfo = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  margin-right: 25px;
  overflow: hidden;
`;

const NextEpisodeTimer = styled.p`
  color: ${nextEpisodeTimerColor};
  font-size: 14px;
  margin: 0;
`;

const NextEpisodeTitle = styled.p<{
  isAudio: boolean;
}>`
  color: ${nextEpisodeInfoColor};
  font-size: 16px;
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: ${props => (props.isAudio ? "nowrap" : "initial")};
`;

const NextEpisodeButton = styled.a`
  align-items: center;
  background: ${playButtonBackgroundColor};
  background-size: 30px;
  border-radius: 20px;
  color: ${darkTheme.ACTION_PRIMARY};
  cursor: pointer;
  display: flex;
  height: 40px;
  justify-content: center;
  margin-left: 10px;
  margin-right: 10px;
  min-width: 40px;
`;

const NextEpisodeClose = styled.button`
  ${buttonStyle};
  background: transparent;
  color: ${darkTheme.ACTION_PRIMARY};
  padding: 10px;
  position: absolute;
  right: 0;
  top: 0;
`;

const NextEpisodeOverlay = ({
  isAudio,
  newPlayerUi,
  nextEpisode,
  update
}: Props) => {
  const { subscribe, useStoreState } = usePlayerStore();
  const durationInSeconds = useStoreState.durationInSeconds();
  const isEnded = useStoreState.isEnded();
  const programId = useStoreState.programId();

  const [overlayVisible, setOverlayVisible] = useState(false);
  const [overlayClosed, setOverlayClosed] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState<number | null>(null);

  const { language } = useEmbedParametersContext();
  const { cuepoints } = useMediaContext();
  const outroCuepoint = useMemo(
    () => findCuepoint("outro", cuepoints),
    [cuepoints]
  );

  const playNextEpisode = useCallback(() => {
    if (overlayClosed) {
      return;
    }
    setOverlayVisible(false);
    setOverlayClosed(true);
    update({
      autoplay: true,
      programId: nextEpisode.id
    });
  }, [overlayClosed, update, nextEpisode.id]);

  useEffect(() => {
    setOverlayClosed(false);
  }, [programId]);

  useEffect(() => {
    if (isEnded) {
      playNextEpisode();
    }
  }, [isEnded, playNextEpisode]);

  useEffect(() => {
    if (overlayClosed || !durationInSeconds) return;

    const unsubscribe = subscribe(
      state => state.currentTime,
      currentTime => {
        const newTimeRemaining = getTimeRemaining(
          currentTime,
          durationInSeconds
        );
        const isVisible =
          newTimeRemaining < END_OFFSET ||
          isCuepointVisible(
            true,
            currentTime,
            outroCuepoint,
            durationInSeconds
          );
        setOverlayVisible(isVisible);
        if (isVisible) {
          setTimeRemaining(newTimeRemaining);
        }
      }
    );

    return () => {
      unsubscribe();
    };
  }, [durationInSeconds, outroCuepoint, overlayClosed, subscribe]);

  const onNextEpisodeButtonClick = (
    e: React.MouseEvent<HTMLAnchorElement, MouseEvent>
  ): void => {
    e.preventDefault();
    playNextEpisode();
  };

  const hideOverlay = (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ): void => {
    e.preventDefault();
    setOverlayClosed(true);
    setOverlayVisible(false);
  };

  return (
    <NextEpisode
      isVisible={overlayVisible}
      isAudio={isAudio}
      newPlayerUi={newPlayerUi}>
      <NextEpisodeButton
        aria-label={i18n("seuraava_jakso", { language })}
        onClick={onNextEpisodeButtonClick}>
        <Play />
      </NextEpisodeButton>
      <NextEpisodeInfo>
        <NextEpisodeTimer>
          {i18n("seuraava_alkaa_xx_s_kuluttua", {
            language,
            s: { seconds: timeRemaining }
          })}
        </NextEpisodeTimer>
        <NextEpisodeTitle isAudio={isAudio}>
          {localizedText(nextEpisode.title, language)}
        </NextEpisodeTitle>
      </NextEpisodeInfo>
      <NextEpisodeClose
        onClick={hideOverlay}
        aria-label={i18n("ala_ohjaa_seuraavaan_jaksoon", { language })}>
        <Close width={20} height={20} />
      </NextEpisodeClose>
    </NextEpisode>
  );
};

export default NextEpisodeOverlay;
